import { Layout, Model, type TabNode } from 'flexlayout-react';
import { memo, type RefObject, useCallback, useEffect, useMemo, useRef } from 'react';

import { CatalogueArticlePreview } from 'app/modules/wallforming/components/catalogue-container/CatalogueArticlePreview';
import { CatalogueContainer } from 'app/modules/wallforming/components/catalogue-container/CatalogueContainer';
import { CopilotChatLayout } from 'app/modules/wallforming/components/copilot/CopilotChatLayout';
import { AutomatedPlacementContainer } from 'app/modules/wallforming/components/setting-container/automated-placement/AutomatedPlacementContainer';
import { settingOptionsModelJson } from 'app/modules/wallforming/components/setting-container/data/settingOptionsModelJson';
import { settingOptionsWithTreeModelJson } from 'app/modules/wallforming/components/setting-container/data/settingOptionsWithTreeModelJson';
import { useSettingEventHandler } from 'app/modules/wallforming/components/setting-container/hooks/useSettingEventHandler';
import { PrefabLayerTree } from 'app/modules/wallforming/components/setting-container/PrefabLayerTree';
import { SettingView } from 'app/modules/wallforming/components/setting-container/types/settingView';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { CopilotFloatingBounds } from 'app/modules/wallforming/components/copilot/types/copilotTypes';

import 'flexlayout-react/style/light.css';
import 'app/modules/wallforming/custom-styles/customFlexlayoutStyle.css';

const IS_DEV_TOOLS = import.meta.env.VITE_DEV_TOOLS === 'true';

const modelDefault = Model.fromJson(settingOptionsModelJson);
const modelWithTree = IS_DEV_TOOLS ? Model.fromJson(settingOptionsWithTreeModelJson) : modelDefault;

interface SettingOptionsLayoutProps {
    copilotFloatingBounds: CopilotFloatingBounds;
    settingModelRef?: RefObject<Model | null>;
    isCopilotOpen: boolean;
    isCopilotFloating: boolean;
    onCopilotClose: () => void;
    onCopilotFloatingBoundsChange: (bounds: CopilotFloatingBounds) => void;
    onCopilotPopOut: () => void;
    onCopilotDock: () => void;
}

export const SettingOptionsLayout = memo(
    ({
        settingModelRef,
        copilotFloatingBounds,
        isCopilotOpen,
        isCopilotFloating,
        onCopilotClose,
        onCopilotFloatingBoundsChange,
        onCopilotPopOut,
        onCopilotDock
    }: SettingOptionsLayoutProps) => {
        const catalogueViewRef = useRef<HTMLDivElement | null>(null);
        const placementViewRef = useRef<HTMLDivElement | null>(null);
        const prevSettingViewRef = useRef<SettingView | null>(usePlacementStore.getState().selectedSettingView);
        // Always call the hook; gate the result with the compile-time constant.
        const prefabLayerTreeOpen = usePlacementStore((state) => IS_DEV_TOOLS && state.prefabLayerTreeOpen);
        const selectedSettingView = usePlacementStore((state) => state.selectedSettingView);

        const activeModel = useMemo(() => (prefabLayerTreeOpen ? modelWithTree : modelDefault), [prefabLayerTreeOpen]);

        useSettingEventHandler({
            catalogueViewRef,
            placementViewRef,
            prevSettingViewRef,
            settingModelRef
        });

        useEffect(() => {
            if (settingModelRef) {
                settingModelRef.current = activeModel;
            }
        }, [activeModel, settingModelRef]);

        const settingOptionsFactory = useCallback((node: TabNode) => {
            const component = node.getComponent();

            if (component === 'settingOptions') {
                return (
                    <div className="p-2 w-full h-full">
                        <CatalogueContainer />
                    </div>
                );
            }
            if (component === 'articlePreview') {
                return (
                    <div className="p-2 w-full h-full">
                        <CatalogueArticlePreview />
                    </div>
                );
            }
            if (component === 'prefabLayerTree') {
                return <PrefabLayerTree />;
            }
        }, []);

        return (
            <div className="settings relative m-0 flex h-full min-h-0 min-w-0 flex-1 flex-col bg-background-default p-0">
                <div
                    ref={catalogueViewRef}
                    className={`z-0 min-h-0 flex-1 overflow-auto p-2 ${
                        selectedSettingView !== SettingView.CATALOGUE ? 'hidden' : ''
                    }`}
                    data-setting-catalogue-view
                >
                    <Layout
                        key={`catalogue-layout-${selectedSettingView}`}
                        factory={settingOptionsFactory}
                        model={activeModel}
                        realtimeResize={false}
                    />
                </div>
                <div
                    ref={placementViewRef}
                    className={`z-0 min-h-0 flex-1 overflow-hidden ${
                        selectedSettingView !== SettingView.AUTOMATED_PLACEMENT ? 'hidden' : ''
                    }`}
                    data-setting-placement-view
                >
                    <AutomatedPlacementContainer />
                </div>
                <div
                    className={
                        isCopilotFloating
                            ? 'contents'
                            : `min-h-0 flex-1 overflow-hidden ${isCopilotOpen ? '' : 'hidden'}`
                    }
                >
                    <CopilotChatLayout
                        floatingBounds={copilotFloatingBounds}
                        isFloating={isCopilotFloating}
                        isOpen={isCopilotOpen}
                        onClose={onCopilotClose}
                        onDock={onCopilotDock}
                        onFloatingBoundsChange={onCopilotFloatingBoundsChange}
                        onPopOut={onCopilotPopOut}
                    />
                </div>
            </div>
        );
    }
);

SettingOptionsLayout.displayName = 'SettingOptionsLayout';
